import {
  useIsMutating,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import ApiHttp from "byzantine/src/ApiHttp";

import {
  TRANSACTION_DETAIL_QUERY_KEY,
  TRANSACTION_SUMMARY_QUERY_KEY,
} from "../constants";

import { TRANSACTION_CATEGORIES_QUERY_KEY } from "./useTransactionCategories";
import { transactionDetailQueryOptions } from "./useTransactionDetail";
import { TRANSACTIONS_LIST_QUERY_KEY } from "./useTransactionsList";

import type { QueryClient, QueryKey } from "@tanstack/react-query";
import type { TransactionsListResponse } from "./useTransactionsList";

export interface CorrectCategoryInput {
  slug: string;
  label: string;
}

export interface UseCorrectTransactionCategoryOptions {
  transactionId: string;
  onError?: () => void;
}

type TaxonomyField = "category" | "sub_category";

const taxonomyMutationKey = (transactionId: string) => [
  "correct-transaction-taxonomy",
  transactionId,
];

export const useIsCorrectingTaxonomy = (transactionId: string) =>
  useIsMutating({ mutationKey: taxonomyMutationKey(transactionId) }) > 0;

const patchFor = (field: TaxonomyField, label: string) =>
  field === "category"
    ? { category: label, sub_category: "" }
    : { sub_category: label };

const applyCorrection = (
  response: TransactionsListResponse | undefined,
  field: TaxonomyField,
  transactionId: string,
  label: string
): TransactionsListResponse | undefined => {
  if (!response) return response;
  return {
    ...response,
    transactions: response.transactions.map((transaction) =>
      transaction.id === transactionId
        ? { ...transaction, ...patchFor(field, label) }
        : transaction
    ),
  };
};

const restoreCorrection = (
  response: TransactionsListResponse | undefined,
  field: TaxonomyField,
  previous: API.Transaction,
  optimisticLabel: string
): TransactionsListResponse | undefined => {
  if (!response) return response;
  return {
    ...response,
    transactions: response.transactions.map((transaction) =>
      transaction.id === previous.id && transaction[field] === optimisticLabel
        ? {
            ...transaction,
            category: previous.category,
            sub_category: previous.sub_category,
          }
        : transaction
    ),
  };
};

// Skips a row whose taxonomy changed again since the restore, so a newer pending save keeps its label.
const syncEnrichment = (
  response: TransactionsListResponse | undefined,
  fresh: API.Transaction,
  previous: API.Transaction
): TransactionsListResponse | undefined => {
  if (!response) return response;
  return {
    ...response,
    transactions: response.transactions.map((transaction) =>
      transaction.id === fresh.id &&
      transaction.category === previous.category &&
      transaction.sub_category === previous.sub_category
        ? {
            ...transaction,
            enrichment_source: fresh.enrichment_source,
            category: fresh.category,
            sub_category: fresh.sub_category,
            merchant: fresh.merchant,
            merchant_description: fresh.merchant_description,
            merchant_matched: fresh.merchant_matched,
          }
        : transaction
    ),
  };
};

// Waits for every pending write, so the refetch cannot overwrite an optimistic edit. The mutation
// settling when this is called still counts as pending.
const refetchListOnceIdle = (queryClient: QueryClient) => {
  const refetch = () =>
    queryClient.invalidateQueries({ queryKey: [TRANSACTIONS_LIST_QUERY_KEY] });
  if (queryClient.isMutating() <= 1) {
    refetch();
    return;
  }
  const unsubscribe = queryClient.getMutationCache().subscribe(() => {
    if (queryClient.isMutating() === 0) {
      unsubscribe();
      refetch();
    }
  });
};

/**
 * Corrects one taxonomy field on a transaction, optimistically.
 *
 * Shared rather than copied per field: the cancel, snapshot and restore rules below are what
 * keep two in-flight corrections from undoing each other, and a second copy would drift.
 */
const useCorrectTransactionTaxonomy = (
  field: TaxonomyField,
  { transactionId, onError }: UseCorrectTransactionCategoryOptions
) => {
  const queryClient = useQueryClient();

  // A setting change since the page loaded can turn this row read-only. Patching only this row,
  // rather than refetching the list, keeps other rows' pending saves from being overwritten.
  const refreshRejectedRow = async (
    previousRows: ReadonlyArray<readonly [QueryKey, API.Transaction]>
  ) => {
    const fresh = await queryClient
      .fetchQuery({
        ...transactionDetailQueryOptions(transactionId),
        staleTime: 0,
      })
      .catch(() => null);
    if (!fresh) return;
    previousRows.forEach(([key, row]) => {
      queryClient.setQueryData<TransactionsListResponse>(key, (response) =>
        syncEnrichment(response, fresh, row)
      );
    });
    // A changed source means the setting flipped, so filtered pages and totals are stale too. A
    // deep-linked row on no cached page gives nothing to compare, so refresh in that case too.
    if (
      previousRows.length === 0 ||
      previousRows.some(
        ([, row]) => row.enrichment_source !== fresh.enrichment_source
      )
    ) {
      refetchListOnceIdle(queryClient);
    }
  };

  return useMutation({
    mutationKey: taxonomyMutationKey(transactionId),
    mutationFn: ({ slug }: CorrectCategoryInput) =>
      ApiHttp.fetch(
        `transactions/${transactionId}`,
        { method: "PUT" },
        { [field]: slug }
      ),

    onMutate: async ({ label }: CorrectCategoryInput) => {
      await queryClient.cancelQueries({
        queryKey: [TRANSACTIONS_LIST_QUERY_KEY],
      });
      await queryClient.cancelQueries({
        queryKey: [TRANSACTION_DETAIL_QUERY_KEY, transactionId],
      });

      const previousRows = queryClient
        .getQueriesData<TransactionsListResponse>({
          queryKey: [TRANSACTIONS_LIST_QUERY_KEY],
        })
        .flatMap(([key, response]) => {
          const row = response?.transactions.find(
            (transaction) => transaction.id === transactionId
          );
          return row ? [[key, row] as const] : [];
        });

      queryClient.setQueriesData<TransactionsListResponse>(
        { queryKey: [TRANSACTIONS_LIST_QUERY_KEY] },
        (response) => applyCorrection(response, field, transactionId, label)
      );

      return { previousRows };
    },

    onError: (_error, { label }: CorrectCategoryInput, context) => {
      context?.previousRows.forEach(([key, row]) => {
        queryClient.setQueryData<TransactionsListResponse>(key, (response) =>
          restoreCorrection(response, field, row, label)
        );
      });
      queryClient.invalidateQueries({
        queryKey: [TRANSACTION_CATEGORIES_QUERY_KEY],
      });
      onError?.();
    },

    // Query-managed, not a side fetch: cancelQueries above cancels a stale one from an earlier
    // settle, and returning the promise keeps isPending true until the panel is actually fresh.
    onSettled: (_data, error, _variables, context) =>
      Promise.all([
        error
          ? refreshRejectedRow(context?.previousRows ?? [])
          : queryClient.invalidateQueries({
              queryKey: [TRANSACTION_DETAIL_QUERY_KEY, transactionId],
            }),
        queryClient.invalidateQueries({
          queryKey: [TRANSACTION_SUMMARY_QUERY_KEY],
        }),
      ]),
  });
};

export const useCorrectTransactionCategory = (
  options: UseCorrectTransactionCategoryOptions
) => useCorrectTransactionTaxonomy("category", options);

export const useCorrectTransactionSubCategory = (
  options: UseCorrectTransactionCategoryOptions
) => useCorrectTransactionTaxonomy("sub_category", options);
